import {
  MindCatalogItem,
  MindCatalogProductDeal,
} from '../models/mind-catalog.model';

export interface MindDisplayPrice {
  displayCents: number;
  originalCents: number;
  promotionMonths: number;
  hasPromotion: boolean;
}

export interface MindOrderMeta {
  originalPriceCents: number;
  displayPriceCents: number;
  promotionMonths: number;
  hasPromotion: boolean;
  installationFee: number;
  productType: string;
  productName: string;
  productId: number;
  extras: unknown[];
  tags: string[];
  deal: MindCatalogProductDeal | null;
}

export const MIND_ORDER_META_FIELD = 'mindOrderMeta';

/** Original (DCC) price in cents for createOrder products[].price */
export function getOriginalPriceCents(item: MindCatalogItem): number {
  const product = item.purchasePayload?.product;
  return (
    item.price?.priceWithDccAjustment ??
    product?.priceMonthlyCents ??
    item.price?.fullMonthlyCents ??
    item.price?.monthlyCents ??
    0
  );
}

export function getPromotionalPeriod(
  item: MindCatalogItem
): { price: number; ate: number } | null {
  const period = item.purchasePayload?.product?.deal?.periods?.[0];
  if (!period || !(period.price > 0) || !(period.ate > 0)) {
    return null;
  }
  return { price: period.price, ate: period.ate };
}

/** UI / schedule display cents (promo when present). */
export function getDisplayPriceCents(item: MindCatalogItem): number {
  const promo = getPromotionalPeriod(item);
  if (promo) {
    return promo.price;
  }
  return item.price?.monthlyCents ?? getOriginalPriceCents(item);
}

export function getDisplayPrice(item: MindCatalogItem): MindDisplayPrice {
  const originalCents = getOriginalPriceCents(item);
  const promo = getPromotionalPeriod(item);
  if (promo) {
    return {
      displayCents: promo.price,
      originalCents,
      promotionMonths: promo.ate,
      hasPromotion: true,
    };
  }
  const monthly = item.price?.monthlyCents ?? originalCents;
  return {
    displayCents: monthly,
    originalCents,
    promotionMonths: 0,
    hasPromotion: false,
  };
}

export function formatBrlFromCents(cents: number): string {
  return (cents / 100).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
}

/** Deal payload for createOrder — only when promotion exists. */
export function buildMindOrderDeal(
  item: MindCatalogItem
): { description: string; periods: { price: number; validity: number }[] } | null {
  const promo = getPromotionalPeriod(item);
  if (!promo) {
    return null;
  }
  const originalCents = getOriginalPriceCents(item);
  const promoFormatted = formatBrlFromCents(promo.price);
  const originalFormatted = formatBrlFromCents(originalCents);
  return {
    description: `pague ${promoFormatted} por ${promo.ate} meses, após pague ${originalFormatted}`,
    periods: [{ price: promo.price, validity: promo.ate * 30 }],
  };
}

export function buildMindOrderMeta(item: MindCatalogItem): MindOrderMeta {
  const product = item.purchasePayload?.product;
  const display = getDisplayPrice(item);
  const extras = item.extras ?? product?.extras ?? [];
  const tags = (product?.tags ?? item.tags ?? []).filter(
    (t): t is string => typeof t === 'string' && t.length > 0
  );

  return {
    originalPriceCents: display.originalCents,
    displayPriceCents: display.displayCents,
    promotionMonths: display.promotionMonths,
    hasPromotion: display.hasPromotion,
    installationFee: product?.installationFee ?? 0,
    productType: (product?.type ?? item.type ?? '').toString().trim(),
    productName: product?.name ?? item.displayName ?? '',
    productId: product?.id ?? Number(item.id) ?? 0,
    extras: Array.isArray(extras) ? extras : [],
    tags,
    deal: product?.deal ?? null,
  };
}

export function parseMindOrderMeta(
  customFields: { label: string; value: string }[] | undefined | null
): MindOrderMeta | null {
  const field = customFields?.find(f => f.label === MIND_ORDER_META_FIELD);
  if (!field?.value) {
    return null;
  }
  try {
    return JSON.parse(field.value) as MindOrderMeta;
  } catch {
    return null;
  }
}

/** UI-facing promo flags/prices (reais) from product.customFields mindOrderMeta. */
export interface MindPromoUi {
  hasPromotion: boolean;
  displayPrice: number;
  originalPrice: number;
  promotionMonths: number;
}

export function getMindPromoUiFromCustomFields(
  customFields: { label: string; value: string }[] | undefined | null
): MindPromoUi | null {
  const meta = parseMindOrderMeta(customFields);
  if (!meta) {
    return null;
  }
  const displayPrice = meta.displayPriceCents / 100;
  const originalPrice = meta.originalPriceCents / 100;
  const hasPromotion =
    !!meta.hasPromotion &&
    meta.displayPriceCents > 0 &&
    meta.originalPriceCents > meta.displayPriceCents;

  return {
    hasPromotion,
    displayPrice,
    originalPrice,
    promotionMonths: meta.promotionMonths ?? 0,
  };
}

export function getMindPromoUiFromProduct(
  product: {
    productType?: string;
    customFields?: { label: string; value: string }[] | null;
  } | null | undefined
): MindPromoUi | null {
  if (!product || product.productType !== 'MIND') {
    return null;
  }
  return getMindPromoUiFromCustomFields(product.customFields);
}
